import { useEffect, useRef, useState } from "react";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { useCapital } from "@/context/CapitalContext";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, SlidersHorizontal, Loader2 } from "lucide-react";

const TONE = {
  "Minimum Spend": {
    ring: "border-rose-200",
    bg: "bg-gradient-to-br from-rose-50 to-white",
    pill: "bg-rose-600",
    title: "text-rose-800",
  },
  "Recommended Annual Plan": {
    ring: "border-amber-200",
    bg: "bg-gradient-to-br from-amber-50 to-white",
    pill: "bg-amber-600",
    title: "text-amber-800",
  },
  "Strategic 24 Month Plan": {
    ring: "border-blue-200",
    bg: "bg-gradient-to-br from-blue-50 to-white",
    pill: "bg-gbe",
    title: "text-gbe",
  },
};

export function Scenarios() {
  const { client } = useCapital();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cap, setCap] = useState(null);
  const [model, setModel] = useState(null);
  const [modelling, setModelling] = useState(false);
  const [live, setLive] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    api.get("/capital/scenarios").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!client) return;
    api.get("/capital/scenarios-live", { params: { client }, timeout: 60000 })
      .then((r) => setLive(r.data)).catch(() => {});
  }, [client]);

  const runModel = (capValue) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setModelling(true);
      try {
        const r = await api.get("/capital/scenario-model", { params: { client, cap: capValue }, timeout: 60000 });
        setModel(r.data);
      } finally { setModelling(false); }
    }, 400);
  };

  useEffect(() => {
    if (!client) return;
    api.get("/capital/scenario-model", { params: { client, cap: 0 }, timeout: 60000 }).then((r) => {
      setModel(r.data);
      const half = Math.round((r.data.pool.budget || 0) / 2);
      setCap(half);
      runModel(half);
    });
    // eslint-disable-next-line
  }, [client]);

  if (loading) return <div className="h-56 bg-white border border-slate-200 rounded-lg shimmer" />;

  const poolBudget = model?.pool?.budget || 0;

  return (
    <div className="space-y-6">
      {/* Interactive budget-cap modeller */}
      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="scenario-modeller">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-gbe" />
          <h3 className="font-display text-base font-semibold text-slate-900">Budget-cap modeller</h3>
          {modelling && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
          <span className="ml-auto text-[11px] text-slate-500">
            Pool: Immediate + 12 Month · {fmtNumber(model?.pool?.count || 0)} assets · {fmtCurrency(poolBudget)}
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Set an annual budget cap — assets are funded in priority order; the rest slip to later horizons.
        </p>
        {cap != null && (
          <div className="mt-4 flex items-center gap-4">
            <Slider value={[cap]} min={0} max={Math.max(poolBudget, 1)} step={Math.max(Math.round(poolBudget / 200), 1000)}
                    onValueChange={([v]) => { setCap(v); runModel(v); }}
                    className="flex-1" data-testid="scenario-cap-slider" />
            <div className="w-36 text-right font-display text-xl font-semibold text-slate-900 tabular-nums">{fmtCurrency(cap)}</div>
          </div>
        )}
        {model && (
          <>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-medium">Funded within cap</div>
                <div className="font-display text-xl font-semibold text-slate-900 tabular-nums mt-1" data-testid="scenario-funded-budget">{fmtCurrency(model.funded.budget)}</div>
                <div className="text-[11px] text-slate-500">{fmtNumber(model.funded.count)} assets</div>
              </div>
              <div className="rounded-md border border-rose-200 bg-rose-50/50 p-3">
                <div className="text-[10px] uppercase tracking-widest text-rose-700 font-medium">Slips to later horizons</div>
                <div className="font-display text-xl font-semibold text-slate-900 tabular-nums mt-1" data-testid="scenario-deferred-budget">{fmtCurrency(model.deferred.budget)}</div>
                <div className="text-[11px] text-slate-500">{fmtNumber(model.deferred.count)} assets</div>
              </div>
              <div className="rounded-md border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">Coverage</div>
                <div className="font-display text-xl font-semibold text-slate-900 tabular-nums mt-1">{model.coverage_pct}%</div>
                <div className="h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-gbe rounded-full transition-all" style={{ width: `${model.coverage_pct}%` }} />
                </div>
              </div>
            </div>
            {model.deferred.count > 0 && (
              <div className="mt-4 grid md:grid-cols-[1fr_280px] gap-4">
                <div className="rounded-md border border-slate-200 overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                    Highest-priority assets that miss the cap
                  </div>
                  <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                    {model.deferred_top.map((a) => (
                      <div key={a.asset_id} className="px-3 py-1.5 text-xs flex items-center gap-2">
                        <span className="flex-1 truncate text-slate-800">{a.asset_name}</span>
                        <span className="text-slate-400 truncate max-w-[140px]">{a.site_name}</span>
                        <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[9px]">{a.horizon === "Immediate Attention" ? "Beyond life" : "12M"}</Badge>
                        <span className="tabular-nums font-medium w-20 text-right">{fmtCurrency(a.cost)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-md border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2">Deferred budget by class</div>
                  <div className="space-y-1.5">
                    {model.deferred_by_class.map((c) => (
                      <div key={c.asset_group} className="flex items-center justify-between text-xs">
                        <span className="text-slate-700 truncate">{c.asset_group}</span>
                        <span className="tabular-nums font-medium">{fmtCurrency(c.budget)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Live client-reconciled scenarios (brief §1.1/§10) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="scenarios-live-grid">
      {(live?.scenarios || []).map((s) => {
        const t = TONE[s.scenario] || TONE["Strategic 24 Month Plan"] || Object.values(TONE)[0];
        return (
          <div key={s.scenario} className={`rounded-xl border ${t.ring} ${t.bg} p-6 relative overflow-hidden`}>
            <span className={`inline-block ${t.pill} text-white text-[10px] uppercase tracking-widest px-2 py-0.5 rounded`}>
              Reconciled to {live?.client}
            </span>
            <h3 className={`mt-3 font-display text-xl font-semibold ${t.title}`}>{s.scenario}</h3>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-display text-3xl font-semibold text-slate-900 tabular-nums">{fmtCurrency(s.capital_required)}</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">{s.assets_included} assets included</div>
            <p className="text-xs text-slate-600 mt-3">{s.description}</p>
            <ul className="mt-4 space-y-1.5 text-xs text-slate-700">
              <li className="flex justify-between"><span>Priority risk addressed</span><span className="font-semibold tabular-nums">{s.priority_risk_addressed_pct}%</span></li>
              <li className="flex justify-between"><span>High-priority assets remaining</span><span className="font-semibold tabular-nums">{s.high_priority_remaining}</span></li>
              <li className="flex justify-between"><span>Deferred exposure</span><span className="font-semibold tabular-nums">{fmtCurrency(s.deferred_exposure)}</span></li>
            </ul>
          </div>
        );
      })}
      </div>
    </div>
  );
}
